import { ReactElement, ReactNode, RefCallback, useCallback, useEffect, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { Item } from '@hh.ru/magritte-ui-tree-selector/Item';
import TreeCollection from '@hh.ru/magritte-ui-tree-selector/collection/treeCollection';
import { AdditionalDefault, IdCollectionPredicate, TreeModel } from '@hh.ru/magritte-ui-tree-selector/collection/types';
import { RenderItem } from '@hh.ru/magritte-ui-tree-selector/types';

import styles from './tree-selector-item.less';

interface ItemsListProps<Additional extends AdditionalDefault> {
    collection: TreeCollection<Additional>;
    leavesOnly?: boolean;
    checkSelectable?: IdCollectionPredicate;
    onExpansion: (id: string) => void;
    expanded: string[];
    selected: string[];
    disabled: string[];
    onChange: (id: string, isSelected: boolean) => void;
    indeterminate: string[];
    singleChoice?: boolean;
    isSearch: boolean;
    renderItemForDesktop?: RenderItem<Additional>;
    renderContentBefore?: (isSearch: boolean) => ReactNode;
}

interface TreeModelItem<Additional extends AdditionalDefault> extends TreeModel<Additional> {
    parentIds: string[];
}

const getNewExpanded = (oldExpanded: string[], newExpanded: string[]): string[] => {
    if (oldExpanded.length >= newExpanded.length) {
        return [];
    }
    return newExpanded.filter((i) => !oldExpanded.includes(i));
};

export const ItemsList = <Additional extends AdditionalDefault>(props: ItemsListProps<Additional>): ReactElement => {
    const {
        collection,
        leavesOnly,
        checkSelectable,
        selected,
        expanded,
        disabled,
        onExpansion,
        indeterminate,
        onChange,
        isSearch,
        singleChoice,
        renderItemForDesktop,
        renderContentBefore,
    } = props;
    const prevExpanded = useRef<string[]>(expanded);
    const expandedIds = useRef<string[]>(expanded);

    const treeItems = useMemo(() => {
        const newItems: TreeModelItem<Additional>[] = [];
        expandedIds.current = getNewExpanded(prevExpanded.current, expanded);
        collection.walk((item, currentParents) => {
            const parentId = currentParents.length ? currentParents[0].id : undefined;
            const isExpandedChild = currentParents.every((parent) => expanded.includes(parent.id));
            if (!parentId || isExpandedChild) {
                newItems.push({
                    ...item,
                    parentIds: currentParents.map((parent) => parent.id),
                });
            }
        });
        return newItems;
    }, [collection, expanded]);
    const marginCache = useRef<Record<string, number>>({});
    const parentRef = useRef<HTMLElement | null>(null);
    const withPromoContent = !!renderContentBefore;

    const getKey = (index: number) => {
        const treeIndex = withPromoContent ? index - 1 : index;
        const treeItem = treeItems[treeIndex];
        if (withPromoContent && index === 0) {
            return 'promo';
        }
        return treeItem.parentIds[0] ? `${treeItem.parentIds[0]}${treeItem.id}` : `${treeItem.id}`;
    };
    const virtualizer = useVirtualizer({
        count: treeItems.length + (withPromoContent ? 1 : 0),
        getScrollElement: () => parentRef.current,
        estimateSize: () => 48,
        getItemKey: getKey,
    });

    const refCallback: RefCallback<HTMLDivElement> = useCallback((node) => {
        parentRef.current = node ? node.parentElement : null;
    }, []);

    const items = virtualizer.getVirtualItems();

    useEffect(() => {
        virtualizer.scrollToOffset(0);
    }, [collection, virtualizer]);

    useEffect(() => {
        prevExpanded.current = expanded;
    }, [expanded]);

    const onAnimationEnd = () => {
        expandedIds.current = [];
    };

    return (
        <div
            ref={refCallback}
            className={styles.virtualizedList}
            style={{
                height: `${virtualizer.getTotalSize()}px`,
            }}
        >
            <div
                className={styles.virtualizedItem}
                style={{
                    transform: `translateY(${items[0]?.start}px)`,
                }}
            >
                {items.map((virtualItem) => {
                    if (withPromoContent && virtualItem.index === 0) {
                        return (
                            <div key={virtualItem.key} ref={virtualizer.measureElement} data-index={virtualItem.index}>
                                {renderContentBefore(isSearch)}
                            </div>
                        );
                    }

                    const treeIndex = withPromoContent ? virtualItem.index - 1 : virtualItem.index;
                    const treeItem = treeItems[treeIndex];
                    const { parentIds, ...item } = treeItem;
                    const parentId = parentIds[0];
                    const wasExpanded = expandedIds.current.some((expandedId) => parentIds.includes(expandedId));

                    return (
                        <div
                            key={virtualItem.key}
                            ref={virtualizer.measureElement}
                            data-index={virtualItem.index}
                            onAnimationEnd={onAnimationEnd}
                            className={wasExpanded ? styles.wasExpanded : undefined}
                        >
                            <Item
                                collection={collection}
                                checkSelectable={checkSelectable}
                                isSelected={selected.includes(item.id)}
                                isExpanded={expanded.includes(item.id)}
                                isIndeterminate={indeterminate.includes(item.id)}
                                isDisabled={disabled.includes(item.id)}
                                parentId={parentId}
                                item={item}
                                leavesOnly={leavesOnly}
                                singleChoice={singleChoice}
                                onExpansion={onExpansion}
                                onChange={onChange}
                                isSearch={isSearch}
                                renderItemForDesktop={renderItemForDesktop}
                                cache={marginCache.current}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
